import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Spinner, Card } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { Get, Post, Put } from '../../../../utils/httpProvider';
import { CHAMCHUYENDE_API, BaseGatewayImage } from '../../../../utils/apiUrl';
import { trangThaiGuiKetQuaValue } from '../../../../utils/define';
import DocumentInput from '../../../../components/document-input/DocumentInput';
import { ConfirmModal } from '../../../../modules/modal/ConfirmModal';

const FieldDisplayFile = ({ label, value }) => {
    const getSafeUrl = (path) => {
        if (!path) return null;
        if (path.startsWith('http')) return path;
        const baseUrl = BaseGatewayImage.endsWith('/') ? BaseGatewayImage.slice(0, -1) : BaseGatewayImage;
        const safePath = path.startsWith('/') ? path : `/${path}`;
        return `${baseUrl}${safePath}`;
    };

    let files = [];
    if (value) {
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
                files = parsed;
            } else {
                files = [{ filePath: value, name: value.split('/').pop() }];
            }
        } catch (e) {
            files = [{ filePath: value, name: value.split('/').pop() }];
        }
    }

    return (
        <Form.Group className="mb-3">
            {label && <Form.Label className="font-weight-bold">{label}</Form.Label>}
            <div className="d-flex flex-wrap">
                {files.length > 0 ? (
                    files.map((file, idx) => {
                        const path = file.filePath || file;
                        let name = file.name || (typeof path === 'string' ? path.split('/').pop() : 'File');
                        name = name.replace(/_\d{14}(?=\.\w+$|$)/, '');
                        return (
                            <a key={idx} href={getSafeUrl(path)} target="_blank" rel="noreferrer" className={`btn btn-outline-primary text-left ${label ? '' : 'btn-sm'} mb-1 mr-1`} style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }} title={name}>
                                <i className="fas fa-paperclip mr-1"></i> {name}
                            </a>
                        );
                    })
                ) : (
                    <span className="text-muted small">Không có file</span>
                )}
            </div>
        </Form.Group>
    );
};

const ChamChuyenDeForm = ({ chuyenDeTienSiId, readOnly = false, isUserView = false }) => {
    const [saving, setSaving] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    
    const [chamChuyenDeId, setChamChuyenDeId] = useState(null);
    const [diemSo, setDiemSo] = useState('');
    const [fileQuyetDinh, setFileQuyetDinh] = useState('');
    const [fileMinhChung, setFileMinhChung] = useState('');
    const [trangThaiGuiKetQua, setTrangThaiGuiKetQua] = useState(trangThaiGuiKetQuaValue.chuaGui);

    const { data: queryRes, refetch, isLoading } = useQuery(
        ['chamChuyenDeData', chuyenDeTienSiId], 
        () => Get(`${CHAMCHUYENDE_API.GET_BY_CHUYENDE_ID}/${chuyenDeTienSiId}`),
        { 
            enabled: !!chuyenDeTienSiId,
            refetchOnWindowFocus: false 
        }
    );

    // Convert enum string từ API về number để khớp với trangThaiGuiKetQuaValue (number)
    const parseTrangThaiGuiKetQua = (val) => {
        if (typeof val === 'number') return val;
        if (val === 'DaGui') return trangThaiGuiKetQuaValue.daGui;
        return trangThaiGuiKetQuaValue.chuaGui;
    };

    useEffect(() => {
        if (queryRes) {
            let item = null;
            if (Array.isArray(queryRes)) {
                item = queryRes[0];
            } else if (queryRes.data && Array.isArray(queryRes.data)) {
                item = queryRes.data[0];
            } else if (queryRes.data?.$values) {
                item = queryRes.data.$values[0];
            } else if (queryRes.data) {
                item = queryRes.data;
            } else {
                item = queryRes;
            }

            if (item && item.id) {
                setChamChuyenDeId(item.id);
                setDiemSo(item.diemSo !== null && item.diemSo !== undefined ? item.diemSo : '');
                setFileQuyetDinh(item.fileQuyetDinh || '');
                setFileMinhChung(item.fileMinhChung || '');
                setTrangThaiGuiKetQua(parseTrangThaiGuiKetQua(item.trangThaiGuiKetQua));
            }
        }
    }, [queryRes]);

    const handleSave = async (isSend = false) => {
        setSaving(true);
        try {
            const payload = {
                ChuyenDeTienSiId: chuyenDeTienSiId,
                DiemSo: diemSo !== '' ? Number(diemSo) : null,
                FileQuyetDinh: fileQuyetDinh || '',
                FileMinhChung: fileMinhChung || '',
                TrangThaiGuiKetQua: isSend ? trangThaiGuiKetQuaValue.daGui : trangThaiGuiKetQua
            };

            if (chamChuyenDeId) {
                await Put(CHAMCHUYENDE_API.UPDATE_ITEM, chamChuyenDeId, { ...payload, Id: chamChuyenDeId });
            } else {
                await Post(CHAMCHUYENDE_API.CREATE, null, payload);
            }

            toast.success(isSend ? 'Gửi kết quả thành công!' : 'Lưu thông tin thành công!');
            await refetch();
        } catch (error) {
            console.error(error);
            toast.error('Có lỗi xảy ra khi lưu kết quả.');
        } finally {
            setSaving(false);
            setShowConfirm(false);
        }
    };

    const handleOpenConfirm = () => {
        setShowConfirm(true);
    };

    if (isLoading) return <div className="text-center p-3"><Spinner animation="border" variant="primary" /></div>;

    const isDaGui = trangThaiGuiKetQua === trangThaiGuiKetQuaValue.daGui;

    if (isUserView && !isDaGui) {
        return (
            <div className="text-center p-5 text-muted">
                <i className="fas fa-file-alt fa-3x mb-3 text-secondary"></i>
                <h5>Chưa có kết quả chấm chuyên đề</h5>
                <p>Kết quả chấm chuyên đề sẽ hiển thị ở đây khi được Phòng HLĐT cập nhật và gửi tới bạn.</p>
            </div>
        );
    }

    return (
        <div className="bg-white p-3 rounded">
            <Card className="mb-3 border-info shadow-sm">
                <Card.Header className="bg-info text-white font-weight-bold">Thông tin chấm chuyên đề</Card.Header>
                <Card.Body>
                    <Row>
                        {((!readOnly && !isUserView) || (diemSo !== null && diemSo !== undefined && diemSo !== '')) && (
                        <Col md={12} className="mb-3">
                            <Form.Group>
                                <Form.Label className="font-weight-bold">Điểm số</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={diemSo}
                                    onChange={(e) => setDiemSo(e.target.value)}
                                    onWheel={(e) => e.target.blur()}
                                    placeholder="Nhập điểm số..."
                                    disabled={readOnly || isUserView}
                                    className={readOnly || isUserView ? "bg-white" : ""}
                                    min="0"
                                    max="10"
                                    step="0.1"
                                />
                            </Form.Group>
                        </Col>
                        )}
                        
                        {((!readOnly && !isUserView) || (fileQuyetDinh && fileQuyetDinh !== '[]' && fileQuyetDinh !== 'null')) && (
                        <Col md={12}>
                            {readOnly || isUserView ? (
                                <FieldDisplayFile label="File quyết định thành lập tiểu ban chấm chuyên đề" value={fileQuyetDinh} />
                            ) : (
                                <Form.Group>
                                    <DocumentInput
                                        labelTitle="File quyết định thành lập tiểu ban chấm chuyên đề"
                                        value={fileQuyetDinh}
                                        onChangeProp={(url) => setFileQuyetDinh(url)}
                                        readOnly={readOnly || isUserView}
                                    />
                                </Form.Group>
                            )}
                        </Col>
                        )}

                        {((!readOnly && !isUserView) || (fileMinhChung && fileMinhChung !== '[]' && fileMinhChung !== 'null')) && (
                        <Col md={12}>
                            {readOnly || isUserView ? (
                                <FieldDisplayFile label="File minh chứng (biên bản, phiếu chấm...)" value={fileMinhChung} />
                            ) : (
                                <Form.Group>
                                    <DocumentInput
                                        labelTitle="File minh chứng (biên bản, phiếu chấm...)"
                                        value={fileMinhChung}
                                        onChangeProp={(url) => setFileMinhChung(url)}
                                        readOnly={readOnly || isUserView}
                                    />
                                </Form.Group>
                            )}
                        </Col>
                        )}
                    </Row>
                </Card.Body>
            </Card>

            {!readOnly && !isUserView && (
                <div className="text-right mt-4">
                    <Button variant="success" onClick={() => handleSave(false)} disabled={saving}>
                        {saving ? <Spinner size="sm" animation="border" className="mr-1" /> : <i className="fas fa-save mr-1"></i>}
                        Lưu thông tin
                    </Button>
                    <Button variant="primary" className="ml-2" onClick={handleOpenConfirm} disabled={saving}>
                        {saving ? <><Spinner size="sm" animation="border" className="mr-2" /> Đang gửi...</> : <><i className="fas fa-paper-plane mr-2"></i> {isDaGui ? 'Gửi lại kết quả' : 'Gửi kết quả'}</>}
                    </Button>
                </div>
            )}

            <ConfirmModal
                isOpen={showConfirm}
                title="Xác nhận gửi kết quả"
                content={
                    <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                        Lưu ý: Khi gửi thông tin này, kết quả sẽ được gửi tới nghiên cứu sinh. Bạn có chắc chắn muốn gửi không?
                    </div>
                }
                labelAccept="Đồng ý gửi"
                handleAccept={() => handleSave(true)}
                handleCancel={() => setShowConfirm(false)}
            />
        </div>
    );
};

export default ChamChuyenDeForm;
